import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, Link as LinkIcon, Unplug, RefreshCw, ExternalLink } from "lucide-react";
import { UsersPanel } from "@/components/UsersPanel.jsx";

export default function Settings() {
  const { user } = useAuth();
  const [google, setGoogle] = useState(null);
  const [wati, setWati] = useState(null);
  const [careers, setCareers] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [g, w, c] = await Promise.all([
      api.get("/oauth/google/status"),
      api.get("/integrations/wati/status"),
      api.get("/integrations/careers-sync/status"),
    ]);
    setGoogle(g.data); setWati(w.data); setCareers(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const flag = params.get("google");
    if (flag === "connected") {
      toast.success(`Google connected · ${params.get("email") || ""}`);
      params.delete("google"); params.delete("email"); setParams(params);
    } else if (flag === "error") {
      toast.error(`Google OAuth error: ${params.get("reason") || "unknown"}`);
      params.delete("google"); params.delete("reason"); setParams(params);
    }
  }, [params]);

  const connectGoogle = async () => {
    try {
      const r = await api.get("/oauth/google/start");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const disconnect = async (email) => {
    if (!window.confirm(`Disconnect ${email} from Google?`)) return;
    await api.delete(`/oauth/google/accounts/${encodeURIComponent(email)}`);
    toast.success("Disconnected");
    load();
  };

  const setConfig = async (patch) => {
    await api.post("/oauth/google/config", patch);
    toast.success("Saved");
    load();
  };

  const syncHRInbox = async () => {
    try {
      const r = await api.post("/hr-email/sync");
      toast.success(`Synced · fetched ${r.data.fetched} · created ${r.data.created}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const probeWati = async () => {
    const r = await api.get("/integrations/wati/status");
    setWati(r.data);
    toast[r.data.probe?.ok ? "success" : "warning"](r.data.probe?.detail || r.data.probe?.error || "Checked");
  };

  const saveCareers = async (patch) => {
    await api.post("/integrations/careers-sync/config", patch);
    toast.success("Saved");
    const r = await api.get("/integrations/careers-sync/status");
    setCareers(r.data);
  };

  const runCareersSync = async () => {
    try {
      const r = await api.post("/integrations/careers-sync/run");
      toast.success(`Synced · created ${r.data.created} · updated ${r.data.updated} · skipped ${r.data.skipped}`);
      const s = await api.get("/integrations/careers-sync/status");
      setCareers(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Sync failed");
    }
  };

  const gAccounts = google?.accounts || [];
  const gCfg = google?.config || {};
  const gDefaults = google?.defaults || {};
  const bookingEmail = gCfg.booking_calendar_email || gDefaults.calendar_emails?.[0] || "";
  const inboxEmail = gCfg.hr_inbox_email || gDefaults.gmail_inbox || "";
  const fromEmail = gCfg.from_email || inboxEmail;

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl font-semibold tracking-tight mb-1">Settings</h1>
      <p className="text-sm text-[#A1A1AA] mb-8">Team access and integrations.</p>

      {/* Google */}
      <section className="tt-card p-6 mb-6" data-testid="integration-google">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="tt-label mb-1">Google · Calendar + Gmail</div>
            <div className="text-sm text-[#A1A1AA]">Connect one Google account per role: a booking calendar for interviews, and an inbox for <span className="font-mono">team@thickandthin.media</span>.</div>
          </div>
          <StatusBadge ok={google?.configured && gAccounts.length > 0} warnIf={!gAccounts.length && google?.configured} />
        </div>

        {!google?.configured && (
          <div className="tt-card bg-[#0F0F0F] p-3 text-xs text-[#F5A524] flex gap-2"><AlertTriangle size={14} className="mt-0.5" /> Server missing <span className="font-mono">GOOGLE_CLIENT_ID</span> or <span className="font-mono">GOOGLE_CLIENT_SECRET</span>.</div>
        )}

        {google?.configured && (
          <>
            <div className="mb-4">
              <button onClick={connectGoogle} className="tt-btn-primary flex items-center gap-2 text-sm" data-testid="google-connect-btn">
                <LinkIcon size={14} /> Connect a Google account
              </button>
              <div className="text-xs text-[#71717A] mt-2 font-mono">redirect uri · {google.redirect_uri}</div>
            </div>

            {gAccounts.length > 0 && (
              <div className="tt-card bg-[#0F0F0F] overflow-hidden mb-5">
                <table className="w-full text-sm">
                  <thead><tr className="text-[#71717A] text-xs font-mono"><th className="text-left px-4 py-2">Account</th><th className="text-left px-4 py-2">Scopes</th><th className="text-right px-4 py-2"></th></tr></thead>
                  <tbody>
                    {gAccounts.map((a) => (
                      <tr key={a.email} className="border-t border-[#1E1E1E]">
                        <td className="px-4 py-3">
                          <div className="font-medium">{a.name || a.email}</div>
                          <div className="font-mono text-xs text-[#71717A]">{a.email}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                          {["calendar", "gmail.modify", "gmail.send"].filter((s) => (a.scope || "").includes(s)).map((s) => <span key={s} className="tt-badge-info mr-1">{s}</span>)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => disconnect(a.email)} className="tt-btn-ghost text-xs text-[#A1A1AA] hover:text-[#EF4444] flex items-center gap-1 ml-auto" data-testid={`google-disconnect-${a.email}`}><Unplug size={12} /> Disconnect</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {gAccounts.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                <ConfigPicker label="Booking calendar"
                  hint="Interview events created here · Meet links auto-generated"
                  value={bookingEmail} accounts={gAccounts}
                  onChange={(v) => setConfig({ booking_calendar_email: v })}
                  testid="cfg-booking-calendar" />
                <ConfigPicker label="HR inbox (polling)"
                  hint="hr-email/sync pulls unread messages from here"
                  value={inboxEmail} accounts={gAccounts}
                  onChange={(v) => setConfig({ hr_inbox_email: v })}
                  testid="cfg-hr-inbox" />
                <ConfigPicker label="Send-as (outbound)"
                  hint="From address for templated candidate emails"
                  value={fromEmail} accounts={gAccounts}
                  onChange={(v) => setConfig({ from_email: v })}
                  testid="cfg-from-email" />
              </div>
            )}

            {gAccounts.length > 0 && inboxEmail && (
              <button onClick={syncHRInbox} className="tt-btn-secondary text-xs mt-5 flex items-center gap-1" data-testid="hr-inbox-sync-btn"><RefreshCw size={12} /> Sync HR inbox now</button>
            )}
          </>
        )}
      </section>

      {/* Wati */}
      <section className="tt-card p-6 mb-6" data-testid="integration-wati">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="tt-label mb-1">Wati · WhatsApp Business</div>
            <div className="text-sm text-[#A1A1AA]">Candidate chat and chain-automation messages. Set <span className="font-mono">WATI_API_ENDPOINT</span> + <span className="font-mono">WATI_API_TOKEN</span> on the server.</div>
          </div>
          <StatusBadge ok={wati?.configured && wati?.probe?.ok} warnIf={wati?.configured && !wati?.probe?.ok} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 text-xs mb-3">
          <div className="tt-card bg-[#0F0F0F] p-3">
            <div className="tt-label mb-1">Endpoint</div>
            <div className="font-mono text-[11px] text-[#A1A1AA] break-all">{wati?.endpoint || <span className="text-[#EF4444]">not set</span>}</div>
          </div>
          <div className="tt-card bg-[#0F0F0F] p-3">
            <div className="tt-label mb-1">Token</div>
            <div className="font-mono text-[11px] text-[#A1A1AA]">{wati?.token_set ? "•••••• set" : <span className="text-[#EF4444]">not set</span>}</div>
          </div>
        </div>
        {wati?.probe && (
          <div className="text-xs font-mono text-[#71717A]">probe · {wati.probe.detail || wati.probe.error || "ok"} {wati.probe.status_code ? `(${wati.probe.status_code})` : ""}</div>
        )}
        {wati?.configured && (
          <button onClick={probeWati} className="tt-btn-secondary text-xs mt-3 flex items-center gap-1" data-testid="wati-probe-btn"><RefreshCw size={12} /> Re-check</button>
        )}
      </section>

      {/* Careers Google Form Sync */}
      <section className="tt-card p-6 mb-6" data-testid="integration-careers">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="tt-label mb-1">Careers Google Form · Sheet sync</div>
            <div className="text-sm text-[#A1A1AA]">Paste the <span className="font-mono">responses sheet URL</span> that your website careers form writes to. We'll auto-detect column headers (Name / Email / Phone / Role / City) and upsert a candidate per row every time you click Sync (or auto every 10 min via the chain cron).</div>
          </div>
          <StatusBadge ok={!!careers?.config?.sheet_url && !!careers?.config?.connected_as} />
        </div>
        <div className="space-y-3">
          <Field label="Google account to read from" hint="Must have access to the sheet. Re-connect Google above if you add a new scope.">
            <select value={careers?.config?.connected_as || ""} onChange={(e) => saveCareers({ connected_as: e.target.value })} className="tt-input text-sm" data-testid="careers-connected-as">
              <option value="">— choose connected account —</option>
              {(google?.accounts || []).map((a) => <option key={a.email} value={a.email}>{a.email}</option>)}
            </select>
          </Field>
          <Field label="Sheet URL" hint="Full URL to the responses sheet (we parse the spreadsheet id).">
            <input className="tt-input font-mono text-xs" placeholder="https://docs.google.com/spreadsheets/d/…"
              defaultValue={careers?.config?.sheet_url || ""}
              onBlur={(e) => e.target.value !== (careers?.config?.sheet_url || "") && saveCareers({ sheet_url: e.target.value })}
              data-testid="careers-sheet-url" />
          </Field>
          <Field label="Tab name" hint="Leave empty for the first/default tab.">
            <input className="tt-input text-sm" placeholder="Form Responses 1"
              defaultValue={careers?.config?.tab_name || ""}
              onBlur={(e) => e.target.value !== (careers?.config?.tab_name || "") && saveCareers({ tab_name: e.target.value })}
              data-testid="careers-tab-name" />
          </Field>
          {careers?.preview && !careers.preview.error && (
            <div className="tt-card bg-[#0F0F0F] p-3 text-xs">
              <div className="tt-label mb-2">Detected headers · {careers.preview.title} <span className="text-[#71717A]">· tab "{careers.preview.tab}"</span></div>
              <div className="flex flex-wrap gap-1">
                {(careers.preview.headers || []).map((h, i) => <span key={i} className="tt-badge-neutral font-mono text-[10px]">{h || `col${i + 1}`}</span>)}
              </div>
            </div>
          )}
          {careers?.preview?.error && (
            <div className="tt-card bg-[#0F0F0F] p-3 text-xs text-[#F5A524] flex gap-2"><AlertTriangle size={13} /> {careers.preview.error}</div>
          )}
          <div className="flex items-center gap-3">
            <button onClick={runCareersSync} disabled={!careers?.config?.sheet_url || !careers?.config?.connected_as} className="tt-btn-primary text-sm flex items-center gap-1 disabled:opacity-50" data-testid="careers-sync-run"><RefreshCw size={13} /> Sync now</button>
            {careers?.config?.last_sync_at && <span className="text-xs text-[#71717A] font-mono">last sync · {new Date(careers.config.last_sync_at).toLocaleString()} · {JSON.stringify(careers.config.last_result || {})}</span>}
            {careers?.config?.sheet_url && <a href={careers.config.sheet_url} target="_blank" rel="noreferrer" className="text-xs text-[#5B9BFF] flex items-center gap-1 ml-auto"><ExternalLink size={11} /> Open sheet</a>}
          </div>
        </div>
      </section>

      {/* Claude */}
      <section className="tt-card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="tt-label mb-1">Claude Sonnet · AI scoring + classification</div>
            <div className="text-sm text-[#A1A1AA]">Used for candidate scoring, quiz generation, and <span className="font-mono">team@</span> intent extraction. Powered by Emergent LLM key.</div>
          </div>
          <StatusBadge ok={true} />
        </div>
      </section>

      <UsersPanel me={user} />
    </div>
  );
}

function StatusBadge({ ok, warnIf }) {
  if (ok) return <span className="tt-badge-pass flex items-center gap-1"><CheckCircle2 size={12} /> Live</span>;
  if (warnIf) return <span className="tt-badge-warn flex items-center gap-1"><AlertTriangle size={12} /> Setup needed</span>;
  return <span className="tt-badge-neutral flex items-center gap-1"><XCircle size={12} /> Not configured</span>;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="tt-label mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-xs text-[#71717A] mt-1">{hint}</div>}
    </div>
  );
}

function ConfigPicker({ label, hint, value, accounts, onChange, testid }) {
  return (
    <div>
      <div className="tt-label mb-1">{label}</div>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="tt-input text-sm" data-testid={testid}>
        <option value="">— choose —</option>
        {accounts.map((a) => <option key={a.email} value={a.email}>{a.email}</option>)}
      </select>
      <div className="text-xs text-[#71717A] mt-1">{hint}</div>
    </div>
  );
}
